import React, { useRef, useEffect, useState } from 'react';

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onPlaceSelected,
  placeholder = "Start typing address...",
  className = "",
  ...props 
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Wait for Google Maps API to load
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait
    
    const checkGoogleMaps = () => {
      attempts++;
      if (window.google?.maps?.places?.Autocomplete) {
        setIsGoogleLoaded(true);
        setLoadError(null);
      } else if (attempts >= maxAttempts) {
        setLoadError("Google Maps could not be loaded. Please enter address manually.");
        setIsGoogleLoaded(false);
      } else {
        setTimeout(checkGoogleMaps, 100);
      }
    };
    checkGoogleMaps();
  }, []);

  // Initialize autocomplete when Google is loaded
  useEffect(() => {
    if (!isGoogleLoaded || !inputRef.current || autocompleteRef.current) {
      return;
    }

    console.log("🚀 Initializing Google Places Autocomplete (JavaScript API)");

    try {
      // Create autocomplete instance
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address']
      });

      // Force the dropdown to render outside any modal/dialog
      if (window.google.maps.places.AutocompleteService) {
        window.google.maps.event.addDomListener(inputRef.current, 'keydown', function(e) {
          // Allow arrow keys and enter for keyboard navigation
          if (e.keyCode === 40 || e.keyCode === 38 || e.keyCode === 13) {
            e.stopPropagation();
          }
        });
      }

      // CRITICAL: Set z-index and pointer events for the dropdown
      setTimeout(() => {
        const pacContainer = document.querySelector('.pac-container');
        if (pacContainer) {
          pacContainer.style.zIndex = '99999';
          pacContainer.style.pointerEvents = 'auto';
          pacContainer.style.position = 'fixed';
          console.log("✅ Set styles for .pac-container");
        }
      }, 100);

      autocompleteRef.current = autocomplete;

      // Listen for place selection
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        console.log("🎉 Place selected!", place);

        if (!place.address_components) {
          console.log("❌ No address components");
          return;
        }

        // Extract address components
        const components = {};
        place.address_components.forEach(component => {
          const types = component.types;
          if (types.includes('street_number')) {
            components.street_number = component.long_name;
          }
          if (types.includes('route')) {
            components.route = component.long_name;
          }
          if (types.includes('locality')) {
            components.city = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            components.state = component.short_name;
          }
          if (types.includes('postal_code')) {
            components.zip_code = component.long_name;
          }
          if (types.includes('country')) {
            components.country = component.short_name;
          }
        });

        const street = `${components.street_number || ''} ${components.route || ''}`.trim();

        console.log("📍 Parsed address:", {
          street,
          city: components.city,
          state: components.state,
          zip_code: components.zip_code
        });

        // Update input to show only street
        if (inputRef.current) {
          inputRef.current.value = street;
        }

        // Call callback with parsed data
        if (onPlaceSelected) {
          onPlaceSelected({
            address: street,
            city: components.city || '',
            state: components.state || '',
            zip_code: components.zip_code || '',
            country: components.country || '',
            formatted_address: place.formatted_address
          });
        }
      });

      console.log("✅ Autocomplete initialized");

      // Add observer to ensure z-index is set when pac-container is added to DOM
      const observer = new MutationObserver(() => {
        const pacContainer = document.querySelector('.pac-container');
        if (pacContainer) {
          if (pacContainer.style.zIndex !== '99999') {
            pacContainer.style.zIndex = '99999';
            pacContainer.style.pointerEvents = 'auto';
            pacContainer.style.position = 'fixed';
            console.log("✅ Set z-index and pointer-events for .pac-container");
          }
          // Also ensure all items have pointer events
          const pacItems = pacContainer.querySelectorAll('.pac-item');
          pacItems.forEach(item => {
            item.style.pointerEvents = 'auto';
            item.style.cursor = 'pointer';
          });
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      return () => {
        observer.disconnect();
      };
    } catch (error) {
      console.error("Failed to initialize Google Places Autocomplete:", error);
      setLoadError("Address autocomplete unavailable. Please enter address manually.");
    }

  }, [isGoogleLoaded, onPlaceSelected]);

  // If there's a load error, show a regular input that still works
  if (loadError) {
    return (
      <div className="space-y-1">
        <input
          type="text"
          placeholder={placeholder}
          onChange={(e) => onChange && onChange(e)}
          className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}
          {...props}
        />
        <p className="text-xs text-amber-600">{loadError}</p>
      </div>
    );
  }

  if (!isGoogleLoaded) {
    return (
      <input
        type="text"
        placeholder="Loading address search..."
        disabled
        className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      placeholder={placeholder}
      autoComplete="off"
      onChange={onChange}
      className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  );
};

export default AddressAutocomplete;
